import React, {useEffect, useMemo, useRef} from "react";
import {useProductListContext} from "../productListProvider/ProductListProvider";
import {useMediaQuery} from "react-responsive";
import window from "../../lib/WindowExtended";
import ProductListFilterMobile from "./ProductListFilterMobile";
import ProductListFilterDesktop from "./ProductListFilterDesktop";

export type RefType = {
  filterHeadRef: React.RefObject<HTMLDivElement>;
  productListRef: React.RefObject<HTMLDivElement>;
};

export type ProductListFilterMobileType = {
  filterNumbers: number;
  toggleFilter: () => void;
};

export type ProductListFilterDesktopType = {
  productListStartPosition: number;
} & ProductListFilterMobileType;

/**
 * Component to render the filter and sort buttons
 * @constructor
 */

const ProductListFilter: React.FC = () => {
  const {state, dispatch} = useProductListContext();
  const elementsRef = useRef<RefType>(null);

  const isMobile = useMediaQuery({query: "(max-width: 940px)"});
  const productListStartPosition =
    (elementsRef.current?.productListRef.current?.getBoundingClientRect().y || 400) + window.scrollY;

  const filterNumbers = useMemo((): number => {
    return Object.values(state.filters).reduce((acc, value) => {
      if (Array.isArray(value)) {
        return acc + value.length;
      } else {
        // case boolean or range filter are set
        return acc + 1;
      }
    }, 0);
  }, [state.filters]);

  const toggleFilter = (): void => {
    dispatch({type: "SHOW_FILTERS", payload: !state.showFilters});
  };

  // Scroll to the top of the list after filtering/sorting
  useEffect(() => {
    if (window.scrollY > productListStartPosition) {
      window.scrollTo({
        top: productListStartPosition - (elementsRef.current?.filterHeadRef.current?.clientHeight || 60),
        behavior: "smooth",
      });
    }
  }, [state.filters, state.orderBy]);

  // mobile layout
  if (isMobile) {
    return <ProductListFilterMobile filterNumbers={filterNumbers} toggleFilter={toggleFilter} ref={elementsRef} />;
  }

  // desktop layout
  return (
    <ProductListFilterDesktop
      filterNumbers={filterNumbers}
      toggleFilter={toggleFilter}
      ref={elementsRef}
      productListStartPosition={productListStartPosition}
    />
  );
};

export default ProductListFilter;
